"use client";

import { usePathname, useRouter } from "next/navigation";

import type { BottomNavigationVariant } from "./types";

import TabBarFramerComponent from "@/framer/environment-v2/tab-bar";

export interface BottomNavigationBarProps {
	variant?: BottomNavigationVariant;
	onCalendarClick?: () => void;
	onEngagementClick?: () => void;
	onContentClick?: () => void;
	onCampaignClick?: () => void;
	onAnalyticsClick?: () => void;
}

const CALENDAR_PATHS = new Set(["/calendar", "/event-details", "/eventdetails"]);

const ENGAGEMENT_PATHS = new Set([
	"/engagement",
	"/postlikes",
	"/postcomments",
	"/commentreply",
	"/autocommentpostsettings",
	"/messages",
	"/messagereply",
	"/mentions",
	"/automessagesettings",
]);

const CONTENT_PATHS = new Set([
	"/content",
	"/conceptdetails",
	"/postdetails",
	"/postdraft",
	"/suggestedpost",
]);

const CAMPAIGN_PATHS = new Set([
	"/campaigns",
	"/campaigndetails",
	"/campaigndraft",
	"/paidcampaign",
	"/organiccampaign",
]);

function resolveVariant(pathname: string): BottomNavigationVariant {
	if (CALENDAR_PATHS.has(pathname)) return "Calendar";
	if (ENGAGEMENT_PATHS.has(pathname)) return "Engagement";
	if (CONTENT_PATHS.has(pathname)) return "Content";
	if (CAMPAIGN_PATHS.has(pathname)) return "Campaigns";
	if (pathname === "/analytics") return "Analytics";
	return "Inactive";
}

export function BottomNavigationBar({
	variant,
	onAnalyticsClick,
	onCampaignClick,
	onCalendarClick,
	onContentClick,
	onEngagementClick,
}: BottomNavigationBarProps) {
	const router = useRouter();
	const pathname = usePathname() ?? "";
	const activeVariant = variant ?? resolveVariant(pathname);

	const handleCalendarClick = onCalendarClick ?? (() => router.push("/calendar"));
	const handleEngagementClick =
		onEngagementClick ?? (() => router.push("/engagement"));
	const handleContentClick = onContentClick ?? (() => router.push("/content"));
	const handleCampaignClick =
		onCampaignClick ?? (() => router.push("/campaigns"));
	const handleAnalyticsClick =
		onAnalyticsClick ?? (() => router.push("/analytics"));

	return (
		<div className="w-full max-w-[393px] overflow-x-clip [&>*]:!max-w-full" data-testid="shared-shell-bottom-nav">
			<TabBarFramerComponent.Responsive
				variants={{ base: activeVariant }}
				analyticsClick={handleAnalyticsClick}
				campaignClick={handleCampaignClick}
				calendarClick={handleCalendarClick}
				contentClick={handleContentClick}
				engagementClick={handleEngagementClick}
				style={{ width: "100%" }}
				width="100%"
			/>
		</div>
	);
}
